import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  CheckCircle2, XCircle, Download, RefreshCw, AlertTriangle,
  DollarSign, Percent, Receipt, ShieldCheck, Smartphone, Zap, Tv, Wallet,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const R = (v) => `R ${(v || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (v) => `${(v || 0).toFixed(2)}%`;

const AnomalyBadge = ({ code }) => {
  const map = {
    zero_cost: { label: "Zero Cost", cls: "bg-amber-100 text-amber-800" },
    missing_vat_category: { label: "No VAT Cat", cls: "bg-slate-100 text-slate-700" },
    negative_gp: { label: "Negative GP", cls: "bg-red-100 text-red-800" },
  };
  const m = map[code] || { label: code, cls: "bg-gray-100 text-gray-700" };
  return <Badge className={`${m.cls} text-[10px] mr-1`}>{m.label}</Badge>;
};

const MatchIndicator = ({ ok }) => (
  ok ? (
    <span className="inline-flex items-center gap-1 text-green-700 text-xs font-medium" data-testid="recon-match">
      <CheckCircle2 className="h-3.5 w-3.5" /> matches Financial Summary
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-red-700 text-xs font-medium" data-testid="recon-mismatch">
      <XCircle className="h-3.5 w-3.5" /> mismatch
    </span>
  )
);

const ReconTile = ({ label, value, matches, icon: Icon, tone = "blue" }) => {
  const toneMap = {
    blue: "from-blue-50 to-blue-100 border-blue-200 text-blue-900",
    green: "from-green-50 to-green-100 border-green-200 text-green-900",
    amber: "from-amber-50 to-amber-100 border-amber-200 text-amber-900",
    slate: "from-slate-50 to-slate-100 border-slate-200 text-slate-900",
  };
  return (
    <Card className={`bg-gradient-to-br ${toneMap[tone]} border`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium uppercase tracking-wider">{label}</span>
          {Icon && <Icon className="h-4 w-4 opacity-70" />}
        </div>
        <p className="text-2xl font-bold" data-testid={`recon-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}>{value}</p>
        {matches !== undefined && <div className="mt-2"><MatchIndicator ok={matches} /></div>}
      </CardContent>
    </Card>
  );
};

const ServiceCard = ({ label, data, icon: Icon }) => (
  <div className="flex items-center justify-between p-3 border rounded-md bg-white">
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-slate-600" />
      <div>
        <p className="text-sm font-semibold text-slate-900">{label}</p>
        <p className="text-xs text-slate-500">{data.count} transaction{data.count === 1 ? "" : "s"}</p>
      </div>
    </div>
    <div className="text-right">
      <p className="text-sm font-semibold text-slate-900">{R(data.revenue_incl_vat)}</p>
      <p className="text-xs text-slate-500">GP {R(data.gross_profit)}</p>
    </div>
  </div>
);

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-ZA", { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const toCSV = (audit) => {
  const rows = [];
  rows.push(["Section", "Transaction #", "Date", "Cashier", "Customer", "Payment", "Type", "Status", "Items", "Total (incl VAT)", "Subtotal (excl VAT)", "VAT", "Cost", "Gross Profit", "GP %", "Anomalies"]);
  const push = (section, list) => list.forEach((r) => rows.push([
    section, r.transaction_number, r.created_at, r.cashier, r.customer, r.payment_method, r.transaction_type, r.status,
    r.items_count, r.total, r.subtotal, r.tax, r.cost ?? "", r.gross_profit ?? "", r.gp_percent ?? "",
    (r.anomalies || []).join("|"),
  ]));
  push("INCLUDED", audit.sales || []);
  push("REFUND", audit.refunds || []);
  push("VOID", audit.voids || []);
  push("PETTY CASH", audit.petty_cash || []);
  const t = audit.totals || {};
  rows.push([]);
  rows.push(["TOTAL — INCLUDED SALES", "", "", "", "", "", "", "", audit.counts?.included, t.revenue_incl_vat, t.revenue_excl_vat, t.vat, t.cogs, t.gross_profit, t.gp_percent, ""]);
  return "\uFEFF" + rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
};

export default function SalesAudit() {
  const [params] = useSearchParams();
  const paramYear = Number(params.get("year"));
  const paramMonth = Number(params.get("month"));
  const [year, setYear] = useState(paramYear || new Date().getFullYear());
  const [month, setMonth] = useState(paramMonth || (new Date().getMonth() + 1));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("included");

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/sales-audit/monthly`, { params: { year, month } });
      setData(r.data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load sales audit");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const download = () => {
    if (!data) return;
    const blob = new Blob([toCSV(data)], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sales-audit-${year}-${String(month).padStart(2, "0")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Sales audit exported");
  };

  const nowY = new Date().getFullYear();
  const t = data?.totals || {};
  const r = data?.reconciliation || {};
  const sb = data?.service_breakdown || {};
  const c = data?.counts || {};

  const table = (rows, showGP = true) => (
    <div className="overflow-x-auto border rounded-md bg-white">
      <table className="w-full text-sm" data-testid="audit-table">
        <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
          <tr>
            <th className="p-2 text-left">Transaction #</th>
            <th className="p-2 text-left">Date</th>
            <th className="p-2 text-left">Type</th>
            <th className="p-2 text-right">Items</th>
            <th className="p-2 text-right">Total (Incl)</th>
            <th className="p-2 text-right">Subtotal</th>
            <th className="p-2 text-right">VAT</th>
            {showGP && <th className="p-2 text-right">Cost</th>}
            {showGP && <th className="p-2 text-right">GP</th>}
            {showGP && <th className="p-2 text-right">GP %</th>}
            {showGP && <th className="p-2 text-left">Flags</th>}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={showGP ? 11 : 7} className="p-6 text-center text-slate-400">No transactions</td></tr>
          )}
          {rows.map((row) => (
            <tr key={row.transaction_number} className="border-t hover:bg-slate-50" data-testid={`audit-row-${row.transaction_number}`}>
              <td className="p-2 font-mono text-xs">{row.transaction_number}</td>
              <td className="p-2 text-xs text-slate-600">{formatDate(row.created_at)}</td>
              <td className="p-2 text-xs">{row.transaction_type || "-"}</td>
              <td className="p-2 text-right">{row.items_count}</td>
              <td className="p-2 text-right font-medium">{R(row.total)}</td>
              <td className="p-2 text-right">{R(row.subtotal)}</td>
              <td className="p-2 text-right text-slate-500">{R(row.tax)}</td>
              {showGP && <td className="p-2 text-right">{R(row.cost)}</td>}
              {showGP && <td className={`p-2 text-right font-medium ${row.gross_profit < 0 ? "text-red-600" : ""}`}>{R(row.gross_profit)}</td>}
              {showGP && <td className="p-2 text-right">{pct(row.gp_percent)}</td>}
              {showGP && <td className="p-2 text-xs">{(row.anomalies || []).map((a) => <AnomalyBadge key={a} code={a} />)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="p-6 space-y-6" data-testid="sales-audit-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="sales-audit-title">Sales Audit</h1>
          <p className="text-sm text-slate-500">Line-level reconciliation of every sale against Financial Summary</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">Period:</span>
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="w-32 h-9 bg-white" data-testid="audit-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MONTHS.map((m, i) => (<SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-24 h-9 bg-white" data-testid="audit-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Array.from({ length: 6 }, (_, i) => nowY - i).map((y) => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="audit-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button onClick={download} disabled={!data || loading} data-testid="audit-export-btn">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      {loading && !data && (
        <div className="flex items-center justify-center py-24">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      )}

      {data && (
        <>
          {/* Reconciliation tiles */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <ReconTile label="Revenue (Incl VAT)" value={R(t.revenue_incl_vat)} matches={r.matches?.revenue_incl_vat} icon={DollarSign} tone="blue" />
            <ReconTile label="Revenue (Excl VAT)" value={R(t.revenue_excl_vat)} matches={r.matches?.revenue_excl_vat} icon={Receipt} tone="slate" />
            <ReconTile label="Cost of Goods" value={R(t.cogs)} matches={r.matches?.cogs} icon={Wallet} tone="amber" />
            <ReconTile label="Gross Profit" value={R(t.gross_profit)} matches={r.matches?.gross_profit} icon={Percent} tone="green" />
          </div>

          {/* Reconciliation status banner */}
          <Card className={`border ${Object.values(r.matches || {}).every(Boolean) ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
            <CardContent className="p-4 flex items-start gap-3">
              {Object.values(r.matches || {}).every(Boolean) ? (
                <>
                  <ShieldCheck className="h-6 w-6 text-green-700 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-green-900" data-testid="recon-status-ok">All figures reconcile with Financial Summary ✓</p>
                    <p className="text-sm text-green-800">
                      Revenue, COGS and Gross Profit computed from every single sale line item match the Financial Summary page byte-for-byte. GP% <strong>{pct(t.gp_percent)}</strong>.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-6 w-6 text-red-700 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-red-900" data-testid="recon-status-mismatch">Mismatch detected between raw sales and Financial Summary</p>
                    <p className="text-sm text-red-800">
                      Audit recomputes from raw sales; Financial Summary shows: Revenue (Incl) audit {R(t.revenue_incl_vat)} vs FS {R(r.financial_summary?.revenue_incl_vat)}, COGS {R(t.cogs)} vs {R(r.financial_summary?.cogs)}, GP {R(t.gross_profit)} vs {R(r.financial_summary?.gross_profit)}.
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Service breakdown */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Service Revenue Breakdown</CardTitle>
              <p className="text-xs text-slate-500">Airtime, electricity, DSTV and vouchers are pass-through — shown separately from stock item sales.</p>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <ServiceCard label="Airtime" icon={Smartphone} data={sb.airtime || { count: 0, revenue_incl_vat: 0, gross_profit: 0 }} />
              <ServiceCard label="Electricity" icon={Zap} data={sb.electricity || { count: 0, revenue_incl_vat: 0, gross_profit: 0 }} />
              <ServiceCard label="DStv" icon={Tv} data={sb.dstv || { count: 0, revenue_incl_vat: 0, gross_profit: 0 }} />
              <ServiceCard label="Vouchers" icon={Receipt} data={sb.voucher || { count: 0, revenue_incl_vat: 0, gross_profit: 0 }} />
              <ServiceCard label="Other services" icon={Wallet} data={sb.other || { count: 0, revenue_incl_vat: 0, gross_profit: 0 }} />
              <div className="p-3 border rounded-md bg-slate-50">
                <p className="text-xs text-slate-500">Stock Revenue (Incl VAT)</p>
                <p className="text-lg font-bold text-slate-900">{R(t.stock_revenue_incl)}</p>
                <p className="text-xs text-slate-500 mt-1">Total service revenue {R(t.service_revenue_incl)}</p>
              </div>
            </CardContent>
          </Card>

          {/* Tabs — Included / Refunds / Voids / Petty */}
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList data-testid="audit-tabs">
              <TabsTrigger value="included" data-testid="tab-included">Included Sales ({c.included || 0})</TabsTrigger>
              <TabsTrigger value="refunds" data-testid="tab-refunds">Refunds ({c.refunds || 0})</TabsTrigger>
              <TabsTrigger value="voids" data-testid="tab-voids">Voids ({c.voids || 0})</TabsTrigger>
              <TabsTrigger value="petty" data-testid="tab-petty">Petty Cash ({c.petty_cash || 0})</TabsTrigger>
            </TabsList>
            <TabsContent value="included" className="mt-3">
              <p className="text-xs text-slate-500 mb-2">Sales counted toward Revenue / COGS / Gross Profit.</p>
              {table(data.sales || [], true)}
            </TabsContent>
            <TabsContent value="refunds" className="mt-3">
              <p className="text-xs text-slate-500 mb-2">Refunds — excluded from GP calculation but shown here so nothing is hidden.</p>
              {table(data.refunds || [], false)}
            </TabsContent>
            <TabsContent value="voids" className="mt-3">
              <p className="text-xs text-slate-500 mb-2">Voided / cancelled transactions — excluded from GP calculation.</p>
              {table(data.voids || [], false)}
            </TabsContent>
            <TabsContent value="petty" className="mt-3">
              <p className="text-xs text-slate-500 mb-2">Petty cash — cash out of till, excluded from revenue.</p>
              {table(data.petty_cash || [], false)}
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
